import { useState, useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import type { Errors } from '@inertiajs/core'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { BusinessShareClass } from '../../types'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  businessId: string
  shareClass: BusinessShareClass | null
  basePath: string
}

export function CapTableShareClassDialog({ open, onOpenChange, businessId, shareClass, basePath }: Props) {
  const [apiError, setApiError] = useState<string | null>(null)

  const { data, setData, post, patch, processing, reset, clearErrors } = useForm({
    name: '',
    shares_authorized: '',
    par_value_cents: '',
    currency: 'usd',
    position: '',
    notes: ''
  })

  useEffect(() => {
    if (open) {
      if (shareClass) {
        setData({
          name: shareClass.name,
          shares_authorized: String(shareClass.shares_authorized),
          par_value_cents: shareClass.par_value_cents != null ? String(shareClass.par_value_cents) : '',
          currency: shareClass.currency,
          position: shareClass.position != null ? String(shareClass.position) : '',
          notes: shareClass.notes || ''
        })
      } else { reset() }
      setApiError(null); clearErrors()
    }
  }, [open, shareClass])

  function handleClose(success: boolean) {
    onOpenChange(false)
    if (!success) { reset(); clearErrors() }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault(); setApiError(null)
    const resourcePath = `${basePath}/businesses/${businessId}/share_classes`
    const payload = {
      ...data,
      shares_authorized: data.shares_authorized ? Number(data.shares_authorized) : '',
      par_value_cents: data.par_value_cents ? Number(data.par_value_cents) : '',
      position: data.position ? Number(data.position) : ''
    }
    const options = { data: { share_class: payload }, onSuccess: () => handleClose(true), onError: (errors: Errors) => setApiError(Object.values(errors).join(', ')) }
    if (shareClass?.id) {
      patch(`${resourcePath}/${shareClass.id}`, options)
    } else {
      post(resourcePath, options)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader className="mb-6"><DialogTitle>{shareClass ? 'Edit Share Class' : 'Add Share Class'}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 px-4">
          {apiError && <div className="text-sm text-destructive bg-destructive/10 rounded-md p-3">{apiError}</div>}
          <div className="space-y-2">
            <Label htmlFor="sc_name">Name <span className="text-destructive">*</span></Label>
            <Input
              id="sc_name"
              placeholder="e.g. Common Stock"
              value={data.name}
              onChange={e => setData('name', e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sc_shares_authorized">Shares Authorized <span className="text-destructive">*</span></Label>
            <Input
              id="sc_shares_authorized"
              type="number"
              min="1"
              placeholder="e.g. 10000000"
              value={data.shares_authorized}
              onChange={e => setData('shares_authorized', e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sc_par_value">Par Value (cents)</Label>
            <Input
              id="sc_par_value"
              type="number"
              min="0"
              placeholder="e.g. 1"
              value={data.par_value_cents}
              onChange={e => setData('par_value_cents', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sc_currency">Currency</Label>
            <Input
              id="sc_currency"
              placeholder="usd"
              value={data.currency}
              onChange={e => setData('currency', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sc_position">Position</Label>
            <Input
              id="sc_position"
              type="number"
              min="0"
              placeholder="Display order"
              value={data.position}
              onChange={e => setData('position', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sc_notes">Notes</Label>
            <Textarea
              id="sc_notes"
              placeholder="Optional notes..."
              value={data.notes}
              onChange={e => setData('notes', e.target.value)}
              rows={3}
            />
          </div>
          <div className="sticky bottom-0 bg-background border-t p-4 -mx-4 mt-8">
            <div className="flex gap-3">
              <Button type="button" variant="outline" className="flex-1" onClick={() => handleClose(false)}>Cancel</Button>
              <Button type="submit" className="flex-1" disabled={processing}>{processing ? 'Saving...' : shareClass ? 'Update' : 'Add Share Class'}</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
